import React, { useEffect, useState, useMemo } from "react";
import { api, useAuth } from "@/AuthContext";
import { toast } from "sonner";
import {
  Plus, X, Trash, UserCircle, PencilSimple, Check, CaretRight,
  ShieldCheck, Eye, ListChecks, Pen, Lock
} from "@phosphor-icons/react";
import PasswordField from "@/components/PasswordField";
import { isPasswordValid } from "@/lib/password";

const ROLE_LABEL = { super_admin:"Super Admin", admin:"Administrador", editor:"Editor", viewer:"Visualizador" };

const MODULE_LABELS = {
  dashboard: "Dashboard",
  installations: "Instalaciones",
  obligations: "Obligaciones",
  documents: "Documentos",
  compliance: "Cumplimiento",
  calendar: "Calendario",
  company_profile: "Perfil de Empresa",
  users: "Usuarios",
  audit: "Auditoría",
  catalogs: "Catálogos",
  super_admin: "Super Admin",
};

const TEMPLATES = [
  { key: "administrador", label: "Administrador", icon: ShieldCheck, desc: "Acceso completo a todos los módulos." },
  { key: "supervisor", label: "Supervisor", icon: ListChecks, desc: "RW operativos, lectura en configuración." },
  { key: "capturista", label: "Capturista", icon: Pen, desc: "Captura en operativos, sin eliminar." },
  { key: "consulta", label: "Consulta", icon: Eye, desc: "Solo lectura en módulos operativos." },
];

function emptyPermissions(modules) {
  return Object.fromEntries(modules.map(m => [m, { read:false, write:false, delete:false }]));
}

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [modules, setModules] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = () => {
    const params = companyFilter ? { company_id: companyFilter } : {};
    api.get("/users", { params }).then(r => setUsers(r.data));
  };
  useEffect(() => { reload(); }, [companyFilter]);
  useEffect(() => {
    api.get("/companies").then(r => setCompanies(r.data)).catch(()=>{});
    api.get("/users/modules").then(r => setModules(r.data.modules || [])).catch(()=>{});
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar este usuario?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Usuario eliminado"); reload(); }
    catch { toast.error("Error"); }
  };

  const companiesById = useMemo(() => Object.fromEntries(companies.map(c => [c.id, c])), [companies]);

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Administración</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Usuarios</h1>
          <p className="text-neutral-500 mt-2">Gestiona accesos, permisos por módulo y empresas asignadas.</p>
        </div>
        <div className="flex gap-2">
          {user?.role === "super_admin" && (
            <select value={companyFilter} onChange={(e)=>setCompanyFilter(e.target.value)} className="border border-neutral-200 rounded-md px-3 py-2.5 text-sm bg-white" data-testid="user-company-filter">
              <option value="">Todas las empresas</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <button onClick={()=>{ setEditing(null); setOpen(true); }} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md" data-testid="new-user-btn">
            <Plus size={18} weight="bold"/> Nuevo usuario
          </button>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-widest text-neutral-500">
              <th className="px-5 py-3 font-semibold">Usuario</th>
              <th className="px-5 py-3 font-semibold">Email</th>
              <th className="px-5 py-3 font-semibold">Rol</th>
              <th className="px-5 py-3 font-semibold">Empresas</th>
              <th className="px-5 py-3 font-semibold">Estado</th>
              <th className="px-5 py-3 font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-md bg-gradient-to-br from-rose-500 to-amber-500 text-white flex items-center justify-center font-bold text-sm">{u.name?.[0]?.toUpperCase()}</div>
                  <span className="font-semibold">{u.name}</span>
                </td>
                <td className="px-5 py-3 text-neutral-600">{u.email}</td>
                <td className="px-5 py-3"><span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-medium">{ROLE_LABEL[u.role]}</span></td>
                <td className="px-5 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(u.companies || []).slice(0,3).map(cid => (
                      <span key={cid} className={`px-2 py-0.5 rounded text-xs font-medium ${cid === u.default_company_id ? 'bg-blue-50 text-blue-700' : 'bg-neutral-100 text-neutral-700'}`}>
                        {companiesById[cid]?.name || cid.slice(0,6)}
                        {cid === u.default_company_id && " ★"}
                      </span>
                    ))}
                    {(u.companies?.length || 0) > 3 && <span className="text-xs text-neutral-400">+{u.companies.length-3}</span>}
                    {(!u.companies || u.companies.length === 0) && u.role !== "super_admin" && <span className="text-xs text-neutral-400">Sin asignar</span>}
                    {u.role === "super_admin" && <span className="text-xs text-neutral-500 italic">Todas (super admin)</span>}
                  </div>
                </td>
                <td className="px-5 py-3">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${u.active!==false?'text-emerald-600':'text-neutral-400'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${u.active!==false?'bg-emerald-500':'bg-neutral-400'}`}></span>
                    {u.active!==false?'Activo':'Inactivo'}
                  </span>
                </td>
                <td className="px-5 py-3 text-right">
                  <button onClick={()=>{ setEditing(u); setOpen(true); }} className="text-blue-600 hover:text-blue-700 mr-3" data-testid={`edit-user-${u.id}`}><PencilSimple size={16}/></button>
                  <button onClick={()=>onDelete(u.id)} className="text-rose-500 hover:text-rose-600" data-testid={`delete-user-${u.id}`}><Trash size={16}/></button>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={6} className="py-12 text-center text-neutral-400">
                <UserCircle size={42} className="mx-auto text-neutral-300 mb-2" weight="duotone"/>
                No hay usuarios. Crea el primero.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && <UserForm me={user} companies={companies} modules={modules} initial={editing} onClose={()=>setOpen(false)} onSuccess={()=>{ setOpen(false); reload(); }}/>}
    </div>
  );
}


function UserForm({ me, companies, modules, initial, onClose, onSuccess }) {
  const isSA = me.role === "super_admin";
  const editable_roles = isSA ? ["super_admin","admin","editor","viewer"] : ["editor","viewer"];
  const allowedCompanies = isSA ? companies : companies; // for admin, backend already returns only their companies

  const initialCompanies = initial?.companies || (initial?.company_id ? [initial.company_id] : []);
  const [tab, setTab] = useState("datos");
  const [structures, setStructures] = useState([]);
  const [f, setF] = useState(initial ? {
    ...initial,
    password: "",
    companies: initialCompanies,
    default_company_id: initial.default_company_id || initial.company_id || initialCompanies[0] || "",
    permissions: mergeWithRole(initial.role, initial.permissions || emptyPermissions(modules), modules),
    access_mode: initial.access_mode || "companies",
    authorized_structure_ids: initial.authorized_structure_ids || [],
    default_structure_id: initial.default_structure_id || "",
    default_node_id: initial.default_node_id || "",
  } : {
    email:"", name:"", password:"",
    role: editable_roles[editable_roles.length-1],
    companies: [], default_company_id: "",
    permissions: roleDefaults(editable_roles[editable_roles.length-1], modules),
    active: true,
    access_mode: "companies",
    authorized_structure_ids: [],
    default_structure_id: "",
    default_node_id: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Cargar estructuras disponibles (solo super_admin / admin ven todas, pero el endpoint ya filtra)
    api.get("/organizations").then(r => setStructures(r.data || [])).catch(() => setStructures([]));
  }, []);

  const toggleStructure = (id) => {
    setF(prev => {
      const has = prev.authorized_structure_ids.includes(id);
      const next = has ? prev.authorized_structure_ids.filter(x => x !== id) : [...prev.authorized_structure_ids, id];
      const def = next.includes(prev.default_structure_id) ? prev.default_structure_id : (next[0] || "");
      return { ...prev, authorized_structure_ids: next, default_structure_id: def, default_node_id: def === prev.default_structure_id ? prev.default_node_id : "" };
    });
  };

  const toggleCompany = (id) => {
    setF(prev => {
      const has = prev.companies.includes(id);
      const next = has ? prev.companies.filter(x => x !== id) : [...prev.companies, id];
      const def = next.includes(prev.default_company_id) ? prev.default_company_id : (next[0] || "");
      return { ...prev, companies: next, default_company_id: def };
    });
  };

  const roleMin = roleDefaults(f.role, modules);
  const togglePerm = (mod, action) => {
    if (roleMin[mod]?.[action]) return;
    setF(prev => {
      const cur = prev.permissions[mod] || { read:false, write:false, delete:false };
      const next = { ...cur, [action]: !cur[action] };
      // si activas escribir o eliminar, fuerza lectura
      if ((action === "write" || action === "delete") && next[action]) next.read = true;
      // si quitas lectura, quitas escribir y eliminar
      if (action === "read" && !next.read) { next.write = false; next.delete = false; }
      return { ...prev, permissions: { ...prev.permissions, [mod]: next } };
    });
  };

  const applyTemplate = async (tplKey) => {
    if (!initial) {
      // local: compute matrix client-side via shape suggestion
      const tplPerms = computeTemplate(tplKey, modules);
      setF(prev => ({ ...prev, permissions: mergeWithRole(prev.role, tplPerms, modules) }));
      toast.success(`Plantilla "${TEMPLATES.find(t=>t.key===tplKey).label}" aplicada`);
      return;
    }
    try {
      const r = await api.post(`/users/${initial.id}/apply-template/${tplKey}`);
      setF(prev => ({ ...prev, permissions: mergeWithRole(prev.role, r.data.permissions, modules) }));
      toast.success(`Plantilla aplicada`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const save = async () => {
    if (!f.name || (!initial && (!f.email || !f.password))) { toast.error("Completa los campos"); return; }
    // Validar contraseña: requerida al crear; opcional al editar (si se ingresó debe cumplir reglas)
    if (!initial && !isPasswordValid(f.password)) {
      toast.error("La contraseña no cumple los requisitos mínimos");
      return;
    }
    if (initial && f.password && !isPasswordValid(f.password)) {
      toast.error("La nueva contraseña no cumple los requisitos mínimos");
      return;
    }
    setBusy(true);
    try {
      if (initial) {
        const payload = {
          name: f.name, role: f.role, active: f.active,
          companies: f.companies, default_company_id: f.default_company_id || null,
          permissions: f.permissions,
          access_mode: f.access_mode || "companies",
          authorized_structure_ids: f.authorized_structure_ids || [],
          default_structure_id: f.default_structure_id || null,
          default_node_id: f.default_node_id || null,
        };
        if (f.password) payload.password = f.password;
        await api.put(`/users/${initial.id}`, payload);
      } else {
        await api.post("/users", {
          email: f.email, name: f.name, password: f.password, role: f.role,
          companies: f.companies, default_company_id: f.default_company_id || null,
          permissions: f.permissions,
          company_id: f.default_company_id || null,
          access_mode: f.access_mode || "companies",
          authorized_structure_ids: f.authorized_structure_ids || [],
          default_structure_id: f.default_structure_id || null,
          default_node_id: f.default_node_id || null,
        });
      }
      toast.success(initial ? "Usuario actualizado" : "Usuario creado");
      onSuccess();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="user-form-modal">
      <div className="bg-white rounded-md w-full max-w-3xl border border-neutral-200 anim-fade-up max-h-[92vh] flex flex-col">
        <div className="flex justify-between items-center p-5 border-b border-neutral-200">
          <h2 className="font-display text-xl font-bold tracking-tight">{initial ? "Editar usuario" : "Nuevo usuario"}</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700"><X size={20}/></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-neutral-200 px-5">
          {[
            { k:"datos", label:"Datos" },
            { k:"accesos", label:"Empresas y Permisos" },
          ].map(t => (
            <button
              key={t.k}
              onClick={()=>setTab(t.k)}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${tab===t.k ? 'border-blue-600 text-blue-700' : 'border-transparent text-neutral-500 hover:text-neutral-800'}`}
              data-testid={`user-form-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-3 overflow-y-auto flex-1">
          {tab === "datos" && (
            <>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Nombre</label>
                <input value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="user-form-name"/>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Email</label>
                <input type="email" disabled={!!initial} value={f.email||""} onChange={(e)=>setF({...f,email:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md disabled:bg-neutral-100" data-testid="user-form-email"/>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">{initial?"Nueva contraseña (opcional)":"Contraseña"}</label>
                <PasswordField
                  value={f.password || ""}
                  onChange={(v) => setF({ ...f, password: v })}
                  required={!initial}
                  testid="user-form-password"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Rol (etiqueta)</label>
                <select value={f.role} onChange={(e)=>{ const role = e.target.value; setF(prev => ({ ...prev, role, permissions: roleDefaults(role, modules) })); toast.info(`Permisos pre-rellenados según rol ${ROLE_LABEL[role] || role}`); }} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="user-form-role">
                  {editable_roles.map(r => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
                </select>
                <p className="text-xs text-neutral-500 mt-1" data-testid="user-form-role-hint">Al elegir el rol se pre-rellenan sus permisos mínimos garantizados. Puedes ampliarlos en la pestaña "Empresas y Permisos".</p>
              </div>
              {initial && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={f.active!==false} onChange={(e)=>setF({...f,active:e.target.checked})} data-testid="user-form-active"/>
                  Usuario activo
                </label>
              )}
            </>
          )}

          {tab === "accesos" && (
            <>
              {/* Modalidad de acceso */}
              {f.role !== "super_admin" && (
                <div>
                  <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Modalidad de acceso organizacional</label>
                  <div className="grid grid-cols-2 gap-2" data-testid="access-mode-selector">
                    <label className={`flex items-start gap-2 p-3 border rounded-md cursor-pointer transition-colors ${f.access_mode === "companies" ? "border-blue-500 bg-blue-50" : "border-neutral-200 hover:border-neutral-300"}`} data-testid="mode-companies">
                      <input type="radio" name="access_mode" checked={f.access_mode === "companies"} onChange={()=>setF({...f, access_mode: "companies"})} className="mt-0.5"/>
                      <div className="flex-1">
                        <div className="font-semibold text-sm">Por empresas / plantas</div>
                        <div className="text-xs text-neutral-500 mt-0.5">Selecciona individualmente las empresas o plantas autorizadas.</div>
                      </div>
                    </label>
                    <label className={`flex items-start gap-2 p-3 border rounded-md cursor-pointer transition-colors ${f.access_mode === "structures" ? "border-amber-500 bg-amber-50" : "border-neutral-200 hover:border-neutral-300"}`} data-testid="mode-structures">
                      <input type="radio" name="access_mode" checked={f.access_mode === "structures"} onChange={()=>setF({...f, access_mode: "structures"})} className="mt-0.5"/>
                      <div className="flex-1">
                        <div className="font-semibold text-sm">Por estructuras organizacionales</div>
                        <div className="text-xs text-neutral-500 mt-0.5">Asigna una o más estructuras completas; el acceso se deriva de sus nodos.</div>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Multi-empresa (modo companies) */}
              {f.access_mode === "companies" && (
              <div>
                <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Empresas / Plantas asignadas</label>
                {f.role === "super_admin" ? (
                  <div className="px-3 py-2.5 bg-neutral-50 border border-neutral-200 rounded-md text-sm text-neutral-600 italic">
                    Super Admin tiene acceso a todas las empresas automáticamente.
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-2 max-h-44 overflow-y-auto border border-neutral-200 rounded-md p-2" data-testid="user-form-companies">
                      {allowedCompanies.map(c => {
                        const checked = f.companies.includes(c.id);
                        return (
                          <label key={c.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-neutral-50 ${checked ? 'bg-blue-50' : ''}`} data-testid={`user-form-company-${c.id}`}>
                            <input type="checkbox" checked={checked} onChange={()=>toggleCompany(c.id)} />
                            <span className="text-sm truncate">{c.name}</span>
                          </label>
                        );
                      })}
                      {allowedCompanies.length === 0 && <div className="col-span-2 text-xs text-neutral-400 p-2">No hay empresas disponibles.</div>}
                    </div>
                    {f.companies.length > 0 && (
                      <div className="mt-2">
                        <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-1">Empresa por defecto al iniciar sesión</label>
                        <select value={f.default_company_id} onChange={(e)=>setF({...f, default_company_id: e.target.value})} className="w-full px-3 py-2 border border-neutral-200 rounded-md bg-white text-sm" data-testid="user-form-default-company">
                          {f.companies.map(cid => {
                            const c = companies.find(x => x.id === cid);
                            return <option key={cid} value={cid}>{c?.name || cid}</option>;
                          })}
                        </select>
                      </div>
                    )}
                  </>
                )}
              </div>
              )}

              {/* Estructuras organizacionales (modo structures) */}
              {f.access_mode === "structures" && f.role !== "super_admin" && (
                <div>
                  <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Estructuras organizacionales asignadas</label>
                  <div className="grid gap-2 max-h-56 overflow-y-auto border border-neutral-200 rounded-md p-2" data-testid="user-form-structures">
                    {structures.length === 0 ? (
                      <div className="text-xs text-neutral-400 p-2">No hay estructuras disponibles. Crea estructuras en el módulo "Estructuras Org."</div>
                    ) : structures.map(s => {
                      const checked = f.authorized_structure_ids.includes(s.id);
                      return (
                        <label key={s.id} className={`flex items-start gap-2 px-2 py-2 rounded cursor-pointer hover:bg-neutral-50 ${checked ? "bg-amber-50" : ""}`} data-testid={`user-form-structure-${s.id}`}>
                          <input type="checkbox" checked={checked} onChange={()=>toggleStructure(s.id)}/>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm">{s.name}</div>
                            <div className="text-xs text-neutral-500 flex flex-wrap gap-1 mt-0.5">
                              {(s.levels || []).map(l => (
                                <span key={l.number} className="px-1.5 py-0 bg-neutral-100 rounded text-[10px]">L{l.number}·{l.label}</span>
                              ))}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {f.authorized_structure_ids.length > 0 && (
                    <div className="mt-2">
                      <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-1">Estructura por defecto al iniciar sesión</label>
                      <select value={f.default_structure_id} onChange={(e)=>setF({...f, default_structure_id: e.target.value, default_node_id: ""})} className="w-full px-3 py-2 border border-neutral-200 rounded-md bg-white text-sm" data-testid="user-form-default-structure">
                        {f.authorized_structure_ids.map(sid => {
                          const s = structures.find(x => x.id === sid);
                          return <option key={sid} value={sid}>{s?.name || sid}</option>;
                        })}
                      </select>
                      <p className="text-xs text-neutral-500 mt-1">Si no se configura un nodo específico, iniciará con "Toda la estructura".</p>
                    </div>
                  )}
                </div>
              )}


              {/* Plantillas */}
              <div className="pt-3">
                <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Plantillas predefinidas</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {TEMPLATES.map(t => {
                    const Ic = t.icon;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={()=>applyTemplate(t.key)}
                        className="flex flex-col items-start gap-1 p-3 border border-neutral-200 rounded-md hover:border-blue-500 hover:bg-blue-50 text-left transition-colors"
                        data-testid={`user-form-template-${t.key}`}
                      >
                        <div className="flex items-center gap-1.5 text-sm font-semibold text-neutral-800">
                          <Ic size={16} weight="duotone" className="text-blue-600"/> {t.label}
                        </div>
                        <span className="text-[11px] text-neutral-500 leading-tight">{t.desc}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Matriz de permisos */}
              <div className="pt-3">
                <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Matriz de permisos por módulo</label>
                <div className="border border-neutral-200 rounded-md overflow-hidden">
                  <table className="w-full text-sm" data-testid="user-form-permissions-matrix">
                    <thead>
                      <tr className="bg-neutral-50 text-xs uppercase tracking-widest text-neutral-500">
                        <th className="px-3 py-2 text-left font-semibold">Módulo</th>
                        <th className="px-3 py-2 text-center font-semibold">Lectura</th>
                        <th className="px-3 py-2 text-center font-semibold">Escritura</th>
                        <th className="px-3 py-2 text-center font-semibold">Eliminación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modules.map(m => {
                        const p = f.permissions[m] || { read:false, write:false, delete:false };
                        const min = roleMin[m] || {};
                        return (
                          <tr key={m} className="border-t border-neutral-100">
                            <td className="px-3 py-2 font-medium text-neutral-800">{MODULE_LABELS[m] || m}</td>
                            {["read","write","delete"].map(action => (
                              <td key={action} className="px-3 py-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={!!p[action] || !!min[action]}
                                  aria-readonly={!!min[action]}
                                  data-locked={min[action] ? "true" : "false"}
                                  title={min[action] ? `Mínimo garantizado por el rol ${ROLE_LABEL[f.role] || f.role}` : undefined}
                                  onChange={()=>togglePerm(m, action)}
                                  data-testid={`perm-${m}-${action}`}
                                  className={`w-4 h-4 accent-blue-600 ${min[action] ? "cursor-not-allowed" : "cursor-pointer"}`}
                                />
                                <span className="inline-block w-3 ml-1 align-middle">{min[action] && <Lock size={12} weight="fill" className="text-blue-600" data-testid={`perm-${m}-${action}-lock`}/>}</span>
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-neutral-500 mt-1.5" data-testid="perm-matrix-hint">Las casillas con candado son el mínimo garantizado por el rol y no pueden quitarse; las demás puedes activarlas para ampliar el acceso. Sin "Lectura", el módulo no aparece en el menú lateral.</p>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-neutral-200 bg-neutral-50">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-md hover:bg-white">Cancelar</button>
          <button onClick={save} disabled={busy} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="user-form-submit">
            {busy ? "Guardando..." : (initial?"Guardar":"Crear")}
          </button>
        </div>
      </div>
    </div>
  );
}


// Mínimo garantizado por rol (espejo de default_permissions_for_role en backend)
const OPERATIVE = ["dashboard","installations","obligations","documents","compliance","calendar","company_profile"];
function roleDefaults(role, modules) {
  const all = Array.from(new Set([...modules, ...OPERATIVE]));
  const mk = (r,w,d) => ({ read:r, write:w, delete:d });
  return Object.fromEntries(all.map(m => {
    if (role === "super_admin") return [m, mk(true,true,true)];
    if (role === "admin") return [m, m === "super_admin" ? mk(false,false,false) : mk(true,true,true)];
    if (!OPERATIVE.includes(m)) return [m, mk(false,false,false)];
    return [m, role === "editor" ? mk(true,true,false) : mk(true,false,false)];
  }));
}
function mergeWithRole(role, perms, modules) {
  const base = roleDefaults(role, modules);
  const out = {};
  Object.keys({ ...base, ...(perms || {}) }).forEach(m => {
    const c = (perms || {})[m] || {}, b = base[m] || {};
    out[m] = { read: !!(c.read || b.read), write: !!(c.write || b.write), delete: !!(c.delete || b.delete) };
  });
  return out;
}

// Plantillas en cliente (espejo del backend para preview inmediato al crear)
function computeTemplate(template, modules) {
  const full = Object.fromEntries(modules.map(m => [m, { read:true, write:true, delete:true }]));
  const none = Object.fromEntries(modules.map(m => [m, { read:false, write:false, delete:false }]));
  if (template === "administrador") return full;
  if (template === "supervisor") {
    const p = { ...none };
    ["dashboard","installations","obligations","documents","compliance","calendar"].forEach(m => { p[m] = { read:true, write:true, delete:false }; });
    ["company_profile","users","audit"].forEach(m => { p[m] = { read:true, write:false, delete:false }; });
    return p;
  }
  if (template === "capturista") {
    const p = { ...none };
    p["dashboard"] = { read:true, write:false, delete:false };
    ["installations","obligations","documents","calendar"].forEach(m => { p[m] = { read:true, write:true, delete:false }; });
    ["compliance","company_profile"].forEach(m => { p[m] = { read:true, write:false, delete:false }; });
    return p;
  }
  if (template === "consulta") {
    const p = { ...none };
    ["dashboard","installations","obligations","documents","compliance","calendar","company_profile"].forEach(m => { p[m] = { read:true, write:false, delete:false }; });
    return p;
  }
  return none;
}
